import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
  RadialBarChart,
  RadialBar,
  Treemap,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
} from "recharts";
import { TrendDown, TrendUp, Buildings, Lightning, Sparkle, Download, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/context/ProjectContext";
import PageHeader from "@/components/PageHeader";
import StatBlock from "@/components/StatBlock";
import OnboardingTour from "@/components/OnboardingTour";
import MiniGantt from "@/components/MiniGantt";
import { fmtKg, fmtNum, fmtUsd } from "@/lib/format";

const COLORS = ["#D4AF37", "#4ADE80", "#38BDF8", "#F87171", "#818CF8", "#FBBF24", "#A78BFA", "#34D399"];

const GRADE_COLOR = { A: "text-eco", B: "text-sky2", C: "text-warn", D: "text-risk" };

function TreemapNode({ x, y, width, height, name, value, fill }) {
  if (width < 0 || height < 0) return null;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={fill} fillOpacity={0.75} stroke="#0F1115" strokeWidth={1} />
      {width > 60 && height > 28 && (
        <>
          <text x={x + 8} y={y + 18} fill="#FFFFFF" fontSize="11" fontFamily="IBM Plex Sans">{name}</text>
          <text x={x + 8} y={y + 32} fill="#FFFFFF" fontSize="10" fontFamily="JetBrains Mono" opacity={0.75}>{(value / 1000).toFixed(2)} t</text>
        </>
      )}
    </g>
  );
}

export default function Dashboard() {
  const { active } = useProjects();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [indices, setIndices] = useState(null);
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    Promise.all([
      api.get(`/projects/${active.id}/summary`),
      api.get(`/projects/${active.id}/indices`),
      api.get(`/projects/${active.id}/impact`),
    ]).then(([s, i, im]) => {
      setSummary(s.data);
      setIndices(i.data);
      setImpact(im.data);
    }).finally(() => setLoading(false));
  }, [active]);

  if (!active) {
    return (
      <div className="p-12 theme-ink-muted">No active project. Create one from the Projects page.</div>
    );
  }

  const intensity = summary?.intensity_kg_per_m2 || 0;
  const target = active.target_intensity_kg_per_m2 || 500;
  const onTrack = intensity <= target;

  const indexList = indices
    ? [
        ["cpi", indices.cpi],
        ["cci", indices.cci],
        ["ccei", indices.ccei],
        ["nzrs", indices.nzrs],
        ["spi", indices.spi],
        ["eci", indices.eci],
        ["cri", indices.cri],
        ["cei", indices.cei],
      ]
    : [];

  const nzrsData = indices ? [{ name: "NZRS", value: indices.nzrs.value, fill: "#D4AF37" }] : [];

  // Radar data: normalised 0-1 for each index (higher = better)
  const radarData = indices
    ? [
        { key: "CPI", value: Math.max(0, 1 - Math.min(2, indices.cpi.value) / 2) },
        { key: "CCEI", value: Math.min(1, indices.ccei.value) },
        { key: "NZRS", value: indices.nzrs.value / 100 },
        { key: "SPI", value: indices.spi.value },
        { key: "CEI", value: indices.cei.value },
        { key: "RISK", value: Math.max(0, 1 - indices.cri.value) },
      ]
    : [];

  const downloadPack = async () => {
    const token = localStorage.getItem("ciq_token");
    toast.message("Preparing Carbon C-Suite Pack…");
    const res = await fetch(`${API_BASE}/projects/${active.id}/csuite-pack/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return toast.error("Could not generate pack");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `csuite-pack-${active.id.slice(0, 8)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Pack downloaded");
  };

  const scheduleMonthly = async () => {
    const email = window.prompt("Project sponsor email for monthly auto-delivery?");
    if (!email) return;
    try {
      await api.post(`/projects/${active.id}/csuite-pack/schedule`, { enabled: true, sponsor_email: email });
      toast.success(`Scheduled. Email delivery is MOCKED until SMTP/Resend is configured.`);
    } catch (e) {
      toast.error("Could not schedule");
    }
  };

  return (
    <div className="animate-fade-up">
      <OnboardingTour />
      {user?.trial_days_remaining !== null && user?.trial_days_remaining !== undefined && user.trial_days_remaining >= 0 && (
        <div
          data-testid="trial-banner"
          className={`px-8 py-2.5 border-b theme-border flex items-center justify-between text-xs ${
            user.trial_days_remaining <= 2 ? "bg-risk/10 text-risk" : "bg-gold/10 text-gold"
          }`}
        >
          <div>
            <span className="font-mono-data uppercase tracking-widest">
              {user.trial_days_remaining > 0
                ? `Free trial · ${user.trial_days_remaining} day${user.trial_days_remaining === 1 ? "" : "s"} remaining`
                : "Trial expired — please subscribe to continue"}
            </span>
          </div>
          <a href="/billing" className="underline">View plans →</a>
        </div>
      )}
      <PageHeader guideId="dashboard"
        overline={`Executive · ${active.country_code} · ${active.sector} · ${active.sub_sector || ""}`}
        title={active.name}
        subtitle={active.description || "Project carbon intelligence snapshot."}
        kicker={`UPDATED ${new Date().toISOString().slice(0, 10)} · ${summary?.boq_count || 0} BOQ ITEMS · ${summary?.monthly_count || 0} MONTHLY ENTRIES`}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="csuite-pack-btn"
              onClick={downloadPack}
              className="flex items-center gap-2 bg-gold text-black px-4 py-2 text-sm font-medium rounded-sm hover:bg-[#e0bc40]"
            >
              <Download size={14} weight="bold" /> Carbon C-Suite Pack
            </button>
            <button
              data-testid="csuite-schedule-btn"
              onClick={scheduleMonthly}
              className="flex items-center gap-2 border theme-border px-4 py-2 text-sm rounded-sm hover:border-gold/40"
            >
              <EnvelopeSimple size={14} /> Auto-send monthly
            </button>
          </div>
        }
      />

      {/* KPI Tiles */}
      <section className="px-8 pt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatBlock
          testid="kpi-embodied"
          label="Embodied (A1–A5)"
          value={fmtKg(summary?.embodied_kg)}
          accent="gold"
          hint={`${summary?.boq_count || 0} BOQ items`}
          tooltip="Σ across all BOQ lines of (quantity × A1-A3 factor) + A4 transport (country factor × km × qty) + A5 construction (2% of A1-A3)."
        />
        <StatBlock
          testid="kpi-operational"
          label="Operational tracked"
          value={fmtKg(summary?.operational_kg)}
          accent="white"
          hint={`${summary?.monthly_count || 0} entries`}
          tooltip="Sum of all monthly entries: amount × source factor (kWh, L diesel, t-km, etc.)."
        />
        <StatBlock
          testid="kpi-intensity"
          label="Intensity"
          value={fmtNum(intensity, 2)}
          unit="kgCO₂e/m²"
          accent={onTrack ? "eco" : "risk"}
          hint={`Target ${target} · ${onTrack ? "On track" : "Above target"}`}
          tooltip="Embodied carbon ÷ floor area. Benchmarks: RICS 600 · LETI 350 · NABERS Excellent 250."
        />
        <StatBlock
          testid="kpi-cost"
          label="Carbon cost exposure"
          value={fmtUsd(summary?.carbon_cost_usd)}
          accent="sky"
          hint={`${summary?.scheme?.name || ""}`}
          tooltip={`Total carbon (t) × project's carbon price ($/tCO₂e). Current scheme: ${summary?.scheme?.name || "—"}.`}
        />
        <StatBlock
          label="Captured"
          value={fmtKg((indices?.captured_t || 0) * 1000)}
          accent="eco"
          hint="lifetime"
          tooltip="Sum of lifetime sequestration across all capture assets (forestry, DAC, biochar, soil, offsets)."
        />
        <StatBlock
          label="Net carbon position"
          value={fmtKg((indices?.net_t || 0) * 1000)}
          accent="warn"
          hint="emit − capture"
          tooltip="Net = total emissions (embodied + operational) − lifetime captured. Aim for negative = net-removal."
        />
        <StatBlock
          label="Carbon cost / capex"
          value={`${fmtNum((indices?.cci?.value || 0) * 100, 2)}%`}
          accent="white"
          hint={fmtUsd(active.cost_usd) + " capex"}
          tooltip="Carbon-cost-to-capex ratio. CCI < 0.5% = comfortable; > 2% = material to project economics."
        />
        <StatBlock
          label="ESG impact"
          value={fmtNum(impact?.trees_required)}
          unit="trees / yr"
          accent="eco"
          hint={`${fmtNum(impact?.cars_off_road)} cars · ${fmtNum(impact?.homes_powered)} homes`}
          tooltip="EPA Greenhouse Gas Equivalencies: trees = tCO₂e × 45 (mature trees/yr); cars off road = tCO₂e × 0.217; homes powered = tCO₂e × 0.197."
        />
      </section>

      {/* Advanced Indices */}
      <section className="px-8 mt-8">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="overline">Advanced indices</div>
            <h2 className="font-serif-display text-2xl theme-ink mt-1">8-index carbon scorecard</h2>
          </div>
          <Sparkle size={16} weight="fill" className="text-gold animate-pulse-gold" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
          {indexList.map(([key, idx]) => (
            <div key={key} data-testid={`index-${key}`} className="border theme-border theme-surface p-4">
              <div className="overline">{key.toUpperCase()}</div>
              <div className="flex items-baseline gap-2 mt-2">
                <div className="font-mono-data text-2xl theme-ink">
                  {key === "nzrs" || key === "eci" ? fmtNum(idx.value, 1) : fmtNum(idx.value, 3)}
                </div>
                <div className={`font-serif-display text-xl ${GRADE_COLOR[idx.grade] || "theme-ink-dim"}`}>{idx.grade}</div>
              </div>
              <div className="text-[10px] theme-ink-dim mt-2 leading-tight">{idx.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Treemap + Radar */}
      <section className="px-8 mt-4 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 border theme-border theme-surface p-6">
          <div className="overline">Embodied carbon · category treemap</div>
          <div className="font-serif-display text-2xl theme-ink mt-1">Where the carbon lives</div>
          <div className="h-72 mt-3">
            <ResponsiveContainer>
              <Treemap
                data={(summary?.by_category || []).map((c, i) => ({ ...c, fill: COLORS[i % COLORS.length] }))}
                dataKey="value"
                stroke="#0F1115"
                aspectRatio={4 / 3}
                content={<TreemapNode />}
              >
                <Tooltip
                  formatter={(value) => [`${(Number(value) / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tCO₂e`, "Embodied"]}
                  labelStyle={{ color: "#D4AF37" }}
                  contentStyle={{ backgroundColor: "#1A1D24", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2 }}
                />
              </Treemap>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="border theme-border theme-surface p-6">
          <div className="overline">Indices radar</div>
          <div className="font-serif-display text-2xl theme-ink mt-1">Performance shape</div>
          <div className="h-72 mt-2">
            <ResponsiveContainer>
              <RadarChart data={radarData}>
                <PolarGrid stroke="rgba(255,255,255,0.08)" />
                <PolarAngleAxis dataKey="key" tick={{ fill: "#71717A", fontSize: 11 }} />
                <Radar dataKey="value" stroke="#D4AF37" fill="#D4AF37" fillOpacity={0.35} />
                <Tooltip
                  formatter={(value) => [`${(Number(value) * 100).toFixed(2)} / 100`, "Score"]}
                  labelStyle={{ color: "#D4AF37" }}
                  contentStyle={{ backgroundColor: "#1A1D24", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2 }}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[10px] theme-ink-dim text-center">Closer to the edge = better. CPI/CRI inverted.</div>
        </div>
      </section>

      {/* 5D Programme Twin (mini-Gantt) */}
      <section className="px-8 mt-4">
        <MiniGantt />
      </section>

      {/* existing area + nzrs charts */}
      <section className="px-8 mt-4 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 border theme-border theme-surface p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="overline">Monthly operational tracking</div>
              <div className="font-serif-display text-2xl theme-ink mt-1">Emissions over time</div>
            </div>
            <div className="text-xs font-mono-data theme-ink-dim">tCO₂e</div>
          </div>
          <div className="h-72">
            {loading ? (
              <div className="h-full flex items-center justify-center theme-ink-dim text-sm">Loading…</div>
            ) : (
              <ResponsiveContainer>
                <AreaChart data={(summary?.monthly || []).map((m) => ({ ...m, total_t: m.total / 1000 }))}>
                  <defs>
                    <linearGradient id="gold" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                  <XAxis dataKey="month" stroke="#71717A" />
                  <YAxis stroke="#71717A" />
                  <Tooltip />
                  <Area type="monotone" dataKey="total_t" stroke="#D4AF37" fill="url(#gold)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="border theme-border theme-surface p-6">
          <div className="overline">Net-Zero readiness</div>
          <div className="font-serif-display text-2xl theme-ink mt-1">NZRS</div>
          <div className="h-56 mt-2 flex items-center justify-center relative">
            <ResponsiveContainer>
              <RadialBarChart innerRadius="65%" outerRadius="100%" data={nzrsData} startAngle={220} endAngle={-40}>
                <RadialBar dataKey="value" cornerRadius={4} background={{ fill: "rgba(255,255,255,0.04)" }} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="font-mono-data text-5xl text-gold">{fmtNum(indices?.nzrs?.value, 0)}</div>
              <div className="text-[10px] theme-ink-dim tracking-widest uppercase mt-1">Net-Zero Readiness</div>
            </div>
          </div>
          <div className="text-xs theme-ink-muted text-center">
            {indices?.nzrs?.grade && (
              <span>Grade <span className={GRADE_COLOR[indices.nzrs.grade]}>{indices.nzrs.grade}</span></span>
            )}
          </div>
        </div>
      </section>

      <section className="px-8 mt-4 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="border theme-border theme-surface p-6">
          <div className="overline">Embodied by category</div>
          <div className="font-serif-display text-2xl theme-ink mt-1">Hotspots</div>
          <div className="h-64 mt-2">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={summary?.by_category || []}
                  innerRadius={50}
                  outerRadius={85}
                  paddingAngle={1}
                  dataKey="value"
                  stroke="#0F1115"
                >
                  {(summary?.by_category || []).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="xl:col-span-2 border theme-border theme-surface p-6">
          <div className="overline">By work package</div>
          <div className="font-serif-display text-2xl theme-ink mt-1">Embodied carbon contribution</div>
          <div className="h-64 mt-3">
            <ResponsiveContainer>
              <BarChart data={summary?.by_package || []} layout="vertical">
                <CartesianGrid stroke="rgba(255,255,255,0.04)" horizontal={false} />
                <XAxis type="number" stroke="#71717A" />
                <YAxis dataKey="name" type="category" stroke="#71717A" width={110} />
                <Tooltip />
                <Bar dataKey="value" fill="#38BDF8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section className="px-8 mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4 pb-12">
        <div className="border theme-border theme-surface p-6">
          <div className="overline">Status signals</div>
          <div className="font-serif-display text-2xl theme-ink mt-1">Project posture</div>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className={`mt-0.5 ${onTrack ? "text-eco" : "text-risk"}`}>
                {onTrack ? <TrendDown size={16} weight="bold" /> : <TrendUp size={16} weight="bold" />}
              </span>
              <div>
                <div className="theme-ink">{onTrack ? "Intensity within target band" : "Intensity exceeds target"}</div>
                <div className="theme-ink-muted text-xs">{fmtNum(intensity, 1)} vs {target} kgCO₂e/m². {onTrack ? "Maintain procurement strategy." : "Investigate top hotspots in Optimizer."}</div>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-sky2"><Buildings size={16} weight="bold" /></span>
              <div>
                <div className="theme-ink">Top hotspot: {summary?.by_category?.[0]?.name || "—"}</div>
                <div className="theme-ink-muted text-xs">{fmtKg(summary?.by_category?.[0]?.value)} — open Optimizer to compare alternatives.</div>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 text-gold"><Lightning size={16} weight="bold" /></span>
              <div>
                <div className="theme-ink">Carbon price applied: {summary?.scheme?.name}</div>
                <div className="theme-ink-muted text-xs">${summary?.carbon_price_usd_per_t}/tCO₂e — change in Carbon Cost.</div>
              </div>
            </li>
          </ul>
        </div>

        <div className="border theme-border theme-surface p-6">
          <div className="overline">Environmental equivalents</div>
          <div className="font-serif-display text-2xl theme-ink mt-1">Why it matters</div>
          <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
            <div className="border theme-border p-3"><div className="overline">Trees / yr</div><div className="font-mono-data text-2xl text-eco mt-1">{fmtNum(impact?.trees_required)}</div></div>
            <div className="border theme-border p-3"><div className="overline">Cars off road</div><div className="font-mono-data text-2xl text-sky2 mt-1">{fmtNum(impact?.cars_off_road)}</div></div>
            <div className="border theme-border p-3"><div className="overline">Homes powered</div><div className="font-mono-data text-2xl text-gold mt-1">{fmtNum(impact?.homes_powered)}</div></div>
            <div className="border theme-border p-3"><div className="overline">Flights avoided</div><div className="font-mono-data text-2xl text-risk mt-1">{fmtNum(impact?.flights_avoided)}</div></div>
          </div>
        </div>
      </section>
    </div>
  );
}
